import { refreshTokenFn } from './auth';
import { getConfig } from '../config/config';

class ApiService {
  constructor() {
    this.baseUrl = getConfig('API.baseUrl');
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  async fetch(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const accessToken = localStorage.getItem('accessToken');

    if (accessToken && !endpoint.includes('/token/')) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`
      };
    }

    try {
      let response = await fetch(url, options);

      // Pour les requêtes de login/register qui renvoient directement les données
      if (endpoint === '/token/') {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Identifiants incorrects');
        }
        return response;
      }

      // Si le token est expiré (401)
      if (response.status === 401 && !endpoint.includes('/token/')) {
        if (this.isRefreshing) {
          // Si un rafraîchissement est déjà en cours, on met la requête en file d'attente
          const newToken = await new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          });

          // Réessayer la requête avec le nouveau token
          options.headers['Authorization'] = `Bearer ${newToken}`;
          response = await fetch(url, options);
        } else {
          this.isRefreshing = true;

          try {
            // Tenter de rafraîchir le token
            const newToken = await refreshTokenFn();
            
            if (newToken) {
              // Mettre à jour le header avec le nouveau token
              options.headers['Authorization'] = `Bearer ${newToken}`;
              // Réessayer la requête originale
              response = await fetch(url, options);
              // Traiter la file d'attente
              this.processQueue(null, newToken);
            } else {
              this.processQueue(new Error('Failed to refresh token'));
              throw new Error('Session expirée');
            }
          } catch (error) {
            this.processQueue(error);
            throw error;
          } finally {
            this.isRefreshing = false;
          }
        }
      }

      // Pour toutes les autres requêtes, vérifier si la réponse est ok
      if (!response.ok) {
        // Lire le corps en texte d'abord (évite "body stream already read" si json() échoue)
        const text = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch (e) {
          throw new Error(text || `Erreur ${response.status}: ${response.statusText}`);
        }
        // Afficher l'erreur avec plus de détails
        const errorMessage = errorData.error || errorData.detail || errorData.message || 'Une erreur est survenue';
        const errorDetails = errorData.details ? ` (${errorData.details})` : '';
        throw new Error(`${errorMessage}${errorDetails}`);
      }

      return response;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Méthodes utilitaires pour les requêtes courantes
  async get(endpoint) {
    return this.fetch(endpoint);
  }

  async post(endpoint, data) {
    return this.fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.fetch(endpoint, {
      method: 'DELETE',
    });
  }
}

export const api = new ApiService(); 